import React, { useEffect } from "react";

import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import CancelButton from 'components/Buttons/CancelButton';
import SaveButton from 'components/Buttons/SaveButton';
import DatePicker from 'components/DatePicker/FormikPicker';
import ModalWrapper from 'components/Modal/FormikModalWrapper';
import { closeModal } from 'components/Modal/modalReducer';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as actions from 'store/actions';
import useStyles from 'styles/forms';
import * as Yup from 'yup';
import { Grid2, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

const getForm = (form) => ({
  valuation: '',
  date: new Date(),
  valuer: '',
  marketvalue: '',
  liquidationvalue: '',
  summary: '',
  assetid: '',
  id: '',
  dcfmethodvalue: '',
  comparativemethodvalue: '',
  insurancemethodvalue: '',
  currentrent: '',
  erv: '',
  discountrate: '',
  exityield: '',
  ...form
});

const validationSchema = Yup.object({
  valuation: Yup.string().required('Valuation is Required!'),
  date: Yup.date().required('Valuation Date is Required!'),
  valuer: Yup.string().nullable(),
  marketvalue: Yup.string().required('Market Value is Required!'),
  liquidationvalue: Yup.string(),
  summary: Yup.string(),
  assetid: Yup.string(),
  id: Yup.string(),
  dcfmethodvalue: Yup.string(),
  comparativemethodvalue: Yup.string(),
  insurancemethodvalue: Yup.string(),
  currentrent: Yup.string(),
  erv: Yup.string(),
  discountrate: Yup.string(),
  exityield: Yup.string()
});

const AddValuation = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  // Data passed from the row click (might be a summary)
  const { valuations } = useSelector((state) => state.modals.modalProps);

  // Loading states and other data from Redux
  const loading = useSelector((state) => state.valuations.loading);
  const { valuers, loaded: valuersLoaded } = useSelector((state) => state.valuations.valuers);
  const { data: asset } = useSelector((state) => state.assets.assetDetails);

  // --- NEW: Select the detailed valuation data from the store ---
  // I am assuming the detailed object is stored at 'state.valuations.valuationDetails'
  const { valuationDetails } = useSelector((state) => state.valuations);

  useEffect(() => {
    dispatch(actions.searchValuer(""));
  }, [dispatch]);

  // --- NEW: useEffect to fetch details when in "edit" mode ---
  useEffect(() => {
    // If the modal is opened for an existing valuation (i.e., it has an id),
    // dispatch an action to get its full details.
    if (valuations?.id) {
      dispatch(actions.getValuationsDetails(valuations.id));
    }
  }, [valuations?.id, dispatch]);


  const handleSave = (form) => {
    dispatch(actions.createValuation(form, asset.id))
      .then(() => {
        if (form.id) {
          dispatch(actions.getValuationsDetails(form.id));
        }
        dispatch(actions.getValuations(asset.id));
      })
      .then(() => {
        toast.success(form.id ? 'Valuation Updated!' : 'Valuation Created!');
        dispatch(closeModal());
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  // --- NEW: Determine the source of truth for the form ---
  // Use the detailed data if it has loaded and matches the current ID.
  // Otherwise, use the summary data from the row props.
  // This prevents the form from being blank while details are fetched.
  const formDataSource = valuationDetails?.id === valuations?.id ? valuationDetails : valuations;

  return (
    <ModalWrapper size="md" header={valuations?.id ? 'Edit Valuation' : 'New Valuation'} xl>
      <Formik
        // --- NEW: Enable re-initialization ---
        // This tells Formik to update the form when the initialValues object changes.
        enableReinitialize
        initialValues={getForm(formDataSource)}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleSave(values);
        }}
      >
        {({ values, isSubmitting, dirty, isValid, touched, errors, submitForm, handleChange, setFieldValue }) => (
          <>
            <DialogContent>
              {/* The rest of your form JSX remains unchanged */}
              <TextField
                fullWidth
                label="Valuation name"
                variant="outlined"
                disabled={loading}
                required
                className={classes.textFieldXL}
                placeholder="Valuation 1"
                name="valuation"
                id="valuation"
                value={values.valuation}
                onChange={handleChange}
                error={touched.valuation && Boolean(errors.valuation)}
                helperText={touched.valuation && errors.valuation}
                slotProps={{
                  input: {
                    classes: {
                      input: classes.input
                    }
                  }
                }}
              />
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                className={classes.box}
              >
                <DatePicker
                  selectedDate={values.date ? new Date(values.date) : new Date()}
                  onChange={handleChange}
                  format="DD/MM/YYYY" label="Date of Valuation" name="date" id="date" />
                <Autocomplete
                  id="valuer"
                  options={[...new Set(valuers)] || []}
                  getOptionLabel={(option) => option || ''}
                  className={classes.autocomplete}
                  autoComplete
                  freeSolo
                  autoHighlight
                  clearOnEscape
                  required
                  disabled={!valuersLoaded}
                  value={values.valuer}
                  onChange={(_, value) => {
                    setFieldValue('valuer', value);
                  }}
                  onInputChange={(_, newValue) => {
                    setFieldValue('valuer', newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Valuer"
                      variant="outlined"
                      error={touched.valuer && Boolean(errors.valuer)}
                      helperText={touched.valuer && errors.valuer}
                    />
                  )}
                />
              </Box>

              <Typography variant="h5">Value Types</Typography>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                className={classes.box}
              >
                <TextField
                  fullWidth
                  label="FMV (€)"
                  variant="outlined"
                  className={classes.textField}
                  type="number"
                  disabled={loading}
                  name="marketvalue"
                  required
                  id="marketvalue"
                  value={values.marketvalue}
                  onChange={handleChange}
                  error={touched.marketvalue && Boolean(errors.marketvalue)}
                  helperText={touched.marketvalue && errors.marketvalue}
                  style={{ width: '48%' }}
                  slotProps={{
                    input: {
                      classes: {
                        input: classes.input
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="Liquidation (€)"
                  variant="outlined"
                  className={classes.textField}
                  type="number"
                  disabled={loading}
                  name="liquidationvalue"
                  id="liquidationvalue"
                  value={values.liquidationvalue}
                  onChange={handleChange}
                  error={touched.liquidationvalue && Boolean(errors.liquidationvalue)}
                  helperText={touched.liquidationvalue && errors.liquidationvalue}
                  style={{ width: '48%' }}
                  slotProps={{
                    input: {
                      classes: {
                        input: classes.input
                      }
                    }
                  }}
                />
              </Box>

              <Grid2 container spacing={2}>
                <Grid2 size={{lg:4}}>
                  <TextField
                    fullWidth
                    label="DCF Method Value (€)"
                    variant="outlined"
                    className={classes.textField}
                    type="number"
                    disabled={loading}
                    name="dcfmethodvalue"
                    id="dcfmethodvalue"
                    value={values.dcfmethodvalue}
                    onChange={handleChange}
                    error={touched.dcfmethodvalue && Boolean(errors.dcfmethodvalue)}
                    helperText={touched.dcfmethodvalue && errors.dcfmethodvalue}
                    slotProps={{
                      input: {
                        classes: {
                          input: classes.input
                        }
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{lg:4}}>
                  <TextField
                    fullWidth
                    label="Comparative Method Value (€)"
                    variant="outlined"
                    className={classes.textField}
                    type="number"
                    disabled={loading}
                    name="comparativemethodvalue"
                    id="comparativemethodvalue"
                    value={values.comparativemethodvalue}
                    onChange={handleChange}
                    error={touched.comparativemethodvalue && Boolean(errors.comparativemethodvalue)}
                    helperText={touched.comparativemethodvalue && errors.comparativemethodvalue}
                    slotProps={{
                      input: {
                        classes: {
                          input: classes.input
                        }
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{lg:4}}>
                  <TextField
                    fullWidth
                    label="Insurance Value (€)"
                    variant="outlined"
                    className={classes.textField}
                    type="number"
                    disabled={loading}
                    name="insurancemethodvalue"
                    id="insurancemethodvalue"
                    value={values.insurancemethodvalue}
                    onChange={handleChange}
                    error={touched.insurancemethodvalue && Boolean(errors.insurancemethodvalue)}
                    helperText={touched.insurancemethodvalue && errors.insurancemethodvalue}
                    slotProps={{
                      input: {
                        classes: {
                          input: classes.input
                        }
                      }
                    }}
                  />
                </Grid2>
              </Grid2>

              <Typography variant="h5">Other KPIs</Typography>

              <Grid2 container spacing={2}>
                <Grid2 size={{lg:4}}>
                  <TextField
                    fullWidth
                    label="Current Rent (EUR/month)"
                    variant="outlined"
                    className={classes.textField}
                    type="number"
                    disabled={loading}
                    name="currentrent"
                    id="currentrent"
                    value={values.currentrent}
                    onChange={handleChange}
                    error={touched.currentrent && Boolean(errors.currentrent)}
                    helperText={touched.currentrent && errors.currentrent}
                    slotProps={{
                      input: {
                        classes: {
                          input: classes.input
                        }
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{lg:4}}>
                  <TextField
                    fullWidth
                    label="ERV (EUR/sqm/month)"
                    variant="outlined"
                    className={classes.textField}
                    type="number"
                    disabled={loading}
                    name="erv"
                    id="erv"
                    value={values.erv}
                    onChange={handleChange}
                    error={touched.erv && Boolean(errors.erv)}
                    helperText={touched.erv && errors.erv}
                    slotProps={{
                      input: {
                        classes: {
                          input: classes.input
                        }
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{lg:4}}>
                  <TextField
                    fullWidth
                    label="Discount Rate (%)"
                    variant="outlined"
                    className={classes.textField}
                    type="number"
                    disabled={loading}
                    name="discountrate"
                    id="discountrate"
                    value={values.discountrate}
                    onChange={handleChange}
                    error={touched.discountrate && Boolean(errors.discountrate)}
                    helperText={touched.discountrate && errors.discountrate}
                    slotProps={{
                      input: {
                        classes: {
                          input: classes.input
                        }
                      }
                    }}
                  />
                </Grid2>
                <Grid2 size={{lg:4}}>
                  <TextField
                    fullWidth
                    label="Exit Yield (%)"
                    variant="outlined"
                    className={classes.textField}
                    type="number"
                    disabled={loading}
                    name="exityield"
                    id="exityield"
                    value={values.exityield}
                    onChange={handleChange}
                    error={touched.exityield && Boolean(errors.exityield)}
                    helperText={touched.exityield && errors.exityield}
                    slotProps={{
                      input: {
                        classes: {
                          input: classes.input
                        }
                      }
                    }}
                  />
                </Grid2>
              </Grid2>

              <TextField
                fullWidth
                label="Report Summary"
                multiline
                variant="outlined"
                className={classes.textFieldXL}
                disabled={loading}
                name="summary"
                id="summary"
                rows={5}
                value={values.summary}
                onChange={handleChange}
                error={touched.summary && Boolean(errors.summary)}
                helperText={touched.summary && errors.summary}
                placeholder="Enter your report"
                slotProps={{
                  input: {
                    classes: {
                      input: classes.input
                    }
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <Box p={2} display="flex">
                <CancelButton onClick={() => dispatch(closeModal())} />
                <SaveButton
                  type="submit"
                  loading={loading}
                  disabled={!dirty || isSubmitting || !isValid}
                  onClick={submitForm}
                  data-testid="valuations-save"
                />
              </Box>
            </DialogActions>
          </>
        )}
      </Formik>
    </ModalWrapper>
  );
};

export default AddValuation;
